import { Box, Drawer, DrawerProps, Grid } from "@mui/material"
import ButtonGradient from "../ButtonGradient";
import ButtonLightGray from "../ButtonLightGray";
import { ISubjectsFilter, Status } from "../../../../Store/Subjects/types";
import { IOperationFilter, IcaoWarnings, LivenessMode, Modality, OperationType } from "../../../../Store/Operations/types";
import SelectCheckmarks from "../SelectCheckmarks";
import FilterDrawerTitle from "../FilterDrawerTitle";
import GridFilterRow from "../grid/GridFilterRow";
import { AccountRole, IAccountFilter } from "../../../../Store/Accounts/types";
import FilterDrawerMoreTextField from "../FilterDrawerMoreTextField";
import React from "react";
import FilterTimeRange from "../FilterTimeRange";

interface IDrawerMoreFilters extends DrawerProps {
    type: "subject" | "operations-face" | "operations-voice" | "operations-fingers" | "account"
    onFilterApply: (filter: ISubjectsFilter) => void,
    resetFilter: () => void,
    filter: ISubjectsFilter | IOperationFilter | IAccountFilter,
    setFilter: React.Dispatch<React.SetStateAction<ISubjectsFilter>> | React.Dispatch<React.SetStateAction<IOperationFilter>> | React.Dispatch<React.SetStateAction<IAccountFilter>>
}

const DrawerMoreFilters: React.FC<IDrawerMoreFilters> = (props) => {

    const isSubject = (): boolean => props.type === "subject"
    const isOperationFace = (): boolean => props.type === "operations-face"
    const isOperationVoice = (): boolean => props.type === "operations-voice"
    const isOperationFingers = (): boolean => props.type === "operations-fingers"
    const isOperation = (): boolean => isOperationFace() || isOperationVoice() || isOperationFingers()
    const isOperationOrSubject = (): boolean => isOperation() || props.type === "subject"
    const isAccount = (): boolean => props.type === "account"

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            props.onFilterApply(props.filter)
        }
    }

    const handleOnClick = () => {
        props.resetFilter();
    }

    return (
        <Drawer
            anchor={props.anchor}
            open={props.open}
            onClose={props.onClose}
            onKeyDown={(e) => handleKeyDown(e)}
        >
            <Box sx={{height: '100vh', maxWidth: '450px', display: 'flex', flexDirection: 'column'}}>
                <Box sx={{flex: "1rem", overflowY: 'auto',px: "1rem"}}>
                    <Grid container>
                        <Grid item xs={8}>
                            <FilterDrawerTitle>
                                More Filters
                            </FilterDrawerTitle>
                        </Grid>
                    </Grid>
                    <Grid container>
                        {isOperationOrSubject() && (
                            <FilterTimeRange
                                lowerBound={(props.filter as IOperationFilter | ISubjectsFilter).dateLowerBound}
                                upperBound={(props.filter as IOperationFilter | ISubjectsFilter).dateUpperBound}
                                setLowerBound={(value) => props.setFilter((prev: any) => ({ ...prev, dateLowerBound: value }))}
                                setUpperBound={(value) => props.setFilter((prev: any) => ({ ...prev, dateUpperBound: value }))}
                            />
                        )}
                        {isOperation() && (
                            <SelectCheckmarks
                                title="Operation type"
                                value={(props.filter as IOperationFilter).operationTypes}
                                setValue={(value) => props.setFilter({ ...props.filter, operationTypes: value })}
                                type={OperationType}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <SelectCheckmarks
                                title="Liveness mode"
                                value={(props.filter as IOperationFilter | ISubjectsFilter).livenessModes}
                                setValue={(value) => props.setFilter({ ...props.filter, livenessModes: value })}
                                type={LivenessMode}
                            />
                        )}
                        {isOperationOrSubject() && (
                            <SelectCheckmarks
                                title="Statuses"
                                value={(props.filter as IOperationFilter | ISubjectsFilter).statuses}
                                setValue={(value) => props.setFilter({ ...props.filter, statuses: value })}
                                type={Status}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <SelectCheckmarks
                                title="Icao warnings"
                                value={(props.filter as IOperationFilter | ISubjectsFilter).icaoWarnings}
                                setValue={(value) => props.setFilter({ ...props.filter, icaoWarnings: value })}
                                type={IcaoWarnings}
                            />
                        )}
                        {isSubject() && (
                            <SelectCheckmarks
                                title="Modality"
                                value={(props.filter as IOperationFilter | ISubjectsFilter).modalities}
                                setValue={(value) => props.setFilter({ ...props.filter, modalities: value })}
                                type={Modality}
                            />
                        )}
                        {isOperationOrSubject() && (
                            <GridFilterRow
                                title="Quality"
                                lowerBound={(props.filter as IOperationFilter | ISubjectsFilter).qualityLowerBound}
                                upperBound={(props.filter as IOperationFilter | ISubjectsFilter).qualityUpperBound}
                                setLowerBound={(value) => props.setFilter({ ...props.filter, qualityLowerBound: value })}
                                setUpperBound={(value) => props.setFilter({ ...props.filter, qualityUpperBound: value })}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <GridFilterRow
                                title="Liveness score"
                                lowerBound={(props.filter as IOperationFilter | ISubjectsFilter).livenessScoreLowerBound}
                                upperBound={(props.filter as IOperationFilter | ISubjectsFilter).livenessScoreUpperBound}
                                setLowerBound={(value) => props.setFilter({ ...props.filter, livenessScoreLowerBound: value })}
                                setUpperBound={(value) => props.setFilter({ ...props.filter, livenessScoreUpperBound: value })}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <GridFilterRow
                                title="Estimated age"
                                lowerBound={(props.filter as IOperationFilter | ISubjectsFilter).ageLowerBound}
                                upperBound={(props.filter as IOperationFilter | ISubjectsFilter).ageUpperBound}
                                setLowerBound={(value) => props.setFilter({ ...props.filter, ageLowerBound: value })}
                                setUpperBound={(value) => props.setFilter({ ...props.filter, ageUpperBound: value })}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <GridFilterRow
                                title="Yaw"
                                lowerBound={(props.filter as IOperationFilter | ISubjectsFilter).yawLowerBound}
                                upperBound={(props.filter as IOperationFilter | ISubjectsFilter).yawUpperBound}
                                setLowerBound={(value) => props.setFilter({ ...props.filter, yawLowerBound: value })}
                                setUpperBound={(value) => props.setFilter({ ...props.filter, yawUpperBound: value })}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <GridFilterRow
                                title="Roll"
                                lowerBound={(props.filter as IOperationFilter | ISubjectsFilter).rollLowerBound}
                                upperBound={(props.filter as IOperationFilter | ISubjectsFilter).rollUpperBound}
                                setLowerBound={(value) => props.setFilter({ ...props.filter, rollLowerBound: value })}
                                setUpperBound={(value) => props.setFilter({ ...props.filter, rollUpperBound: value })}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <GridFilterRow
                                title="Pitch"
                                lowerBound={(props.filter as IOperationFilter | ISubjectsFilter).pitchLowerBound}
                                upperBound={(props.filter as IOperationFilter | ISubjectsFilter).pitchUpperBound}
                                setLowerBound={(value) => props.setFilter({ ...props.filter, pitchLowerBound: value })}
                                setUpperBound={(value) => props.setFilter({ ...props.filter, pitchUpperBound: value })}
                            />
                        )}
                        {(isSubject() || isOperationFace()) && (
                            <SelectCheckmarks
                                title="Role"
                                value={(props.filter as IAccountFilter).accountRoles}
                                setValue={(value) => props.setFilter({ ...props.filter, accountRoles: value })}
                                type={AccountRole}
                            />
                        )}
                        {isAccount() && (
                            <FilterDrawerMoreTextField
                                title="UUID"
                                value={(props.filter as IAccountFilter).uuid}
                                setValue={(value) => props.setFilter({ ...props.filter, uuid: value })}
                            />
                        )}
                    </Grid>
                </Box>
                <Box sx={{padding: "1rem", paddingTop: "2rem"}}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <ButtonLightGray
                                onClick={handleOnClick}
                                variant="text"
                                fullWidth
                            >
                                Clear filter
                            </ButtonLightGray>
                        </Grid>
                        <Grid item xs={6}>
                            <ButtonGradient
                                onClick={() => props.onFilterApply(props.filter)}
                                variant="contained"
                                fullWidth
                            >
                                Apply
                            </ButtonGradient>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Drawer>
    );
}

export default DrawerMoreFilters